// The address history of the client application (SEP-293.5).
//
// The firm needs the addresses of the client for the last 3 years. Each
// address has a move-in date. An address ends on the day that the next
// address starts, so the form asks for no end date.
//
//   - The current address always shows.
//   - The second address (the `previous_address_*` ids) shows when the
//     client moved in to the current address less than 36 months before
//     today.
//   - The third address (the `third_address_*` ids) shows when the second
//     address shows and the client moved in to it less than 36 months before
//     today. It is the last address: the form has no fourth address.
//   - While a move-in date is blank, the next address stays open if the
//     draft already holds an answer for it. A draft of form version 2.0 can
//     hold a previous address and no move-in date. The prune must not drop
//     that address: the client gives the dates instead.
//
// The rules compare dates with `today`, and the caller gives `today`. The
// contract never reads the clock. When `today` is null, a visibility rule
// keeps its question shown and a form rule gives no error.

import type {
    ClientApplicationFormRule,
    ClientApplicationRuleContext,
    ClientApplicationRuleError,
    ClientApplicationVisibilityRule,
} from './client-application-questions';

/** The number of months of address history that the firm needs. */
export const CLIENT_APPLICATION_ADDRESS_HISTORY_MONTHS = 36;

/** The move-in date of each address, from the current address to the third. */
export const CLIENT_APPLICATION_MOVED_IN_DATE_IDS = {
    current: 'current_address_moved_in_date',
    second: 'previous_address_moved_in_date',
    third: 'third_address_moved_in_date',
} as const;

/** The name of the rule that opens the second address. */
export const ADDRESS_HISTORY_NEEDS_SECOND = 'address_history_needs_second';
/** The name of the rule that opens the third address. */
export const ADDRESS_HISTORY_NEEDS_THIRD = 'address_history_needs_third';

/** The message when the three addresses cover less than 36 months. */
export const ADDRESS_HISTORY_COVERAGE_MESSAGE =
    'Your three addresses cover less than 3 years. Please check the move-in dates. If you lived at a fourth address, please contact us.';

/** The message for a move-in date after today. */
export const ADDRESS_HISTORY_FUTURE_MESSAGE = 'Enter a date that is not after today.';

/** The message for a move-in date that is not before the move-in date of the next address. */
export function addressHistoryOrderMessage(next: 'current' | 'previous'): string {
    return `This date must be before the date you moved in to your ${next} address.`;
}

/** Whether a value is a real calendar date, `YYYY-MM-DD`. */
function calendarDate(value: unknown): string | null {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString().slice(0, 10) === value ? value : null;
}

/**
 * The date a number of months before a date, `YYYY-MM-DD`.
 *
 * The day stays the same. When that month has no such day (29 February in a
 * year that is not a leap year, or the 31st of a short month), the result is
 * the last day of that month.
 */
export function calendarDateMonthsBefore(date: string, months: number): string {
    const [year, month, day] = date.split('-').map(Number) as [number, number, number];
    const index = year * 12 + (month - 1) - months;
    const targetYear = Math.floor(index / 12);
    const targetMonth = index - targetYear * 12;
    const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
    const targetDay = Math.min(day, lastDay);
    return `${String(targetYear).padStart(4, '0')}-${String(targetMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
}

/**
 * Whether a client who moved in on `movedIn` lived there for 36 months or
 * more on `today`. Exactly 36 months is enough.
 */
export function coversAddressHistory(movedIn: string, today: string): boolean {
    return movedIn <= calendarDateMonthsBefore(today, CLIENT_APPLICATION_ADDRESS_HISTORY_MONTHS);
}

/**
 * Whether one address opens the next address. It opens the next one when
 * its move-in date is a real date less than 36 months before today. A blank
 * date opens nothing, because the client has not said yet.
 */
function opensNext(value: unknown, today: string): boolean {
    const movedIn = calendarDate(value);
    return movedIn !== null && !coversAddressHistory(movedIn, today);
}

/**
 * Whether the answers hold a value for one address, from its id prefix. A
 * blank string, an empty list and a null are not values.
 */
function holdsAddress(answers: Readonly<Record<string, unknown>>, prefix: string): boolean {
    return Object.entries(answers).some(([id, value]) => {
        if (!id.startsWith(prefix)) return false;
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim() !== '';
        if (Array.isArray(value)) return value.length > 0;
        return true;
    });
}

/**
 * Whether one address opens the next one. A real date opens it when it is
 * less than 36 months before today. A blank date keeps the next address open
 * only when the answers already hold that address.
 */
function opensAddress(
    answers: Readonly<Record<string, unknown>>,
    movedInId: string,
    nextPrefix: string,
    today: string,
): boolean {
    const value = answers[movedInId];
    if (calendarDate(value) === null && holdsAddress(answers, nextPrefix)) return true;
    return opensNext(value, today);
}

/** The second address shows while the current address covers less than 36 months. */
export const addressHistoryNeedsSecond: ClientApplicationVisibilityRule = (answers, context) => {
    if (context.today === null) return true;
    return opensAddress(
        answers,
        CLIENT_APPLICATION_MOVED_IN_DATE_IDS.current,
        'previous_address_',
        context.today,
    );
};

/** The third address shows while the current and the second address cover less than 36 months. */
export const addressHistoryNeedsThird: ClientApplicationVisibilityRule = (answers, context) => {
    if (context.today === null) return true;
    return (
        addressHistoryNeedsSecond(answers, context) &&
        opensAddress(
            answers,
            CLIENT_APPLICATION_MOVED_IN_DATE_IDS.second,
            'third_address_',
            context.today,
        )
    );
};

/** The move-in dates that show now, from the current address to the third. */
function shownMovedInDates(
    answers: Readonly<Record<string, unknown>>,
    context: ClientApplicationRuleContext,
): { readonly id: string; readonly date: string | null }[] {
    const ids = CLIENT_APPLICATION_MOVED_IN_DATE_IDS;
    const shown: { id: string; date: string | null }[] = [
        { id: ids.current, date: calendarDate(answers[ids.current]) },
    ];
    if (addressHistoryNeedsSecond(answers, context)) {
        shown.push({ id: ids.second, date: calendarDate(answers[ids.second]) });
        if (addressHistoryNeedsThird(answers, context)) {
            shown.push({ id: ids.third, date: calendarDate(answers[ids.third]) });
        }
    }
    return shown;
}

/**
 * The form rules of the address history.
 *
 *   1. A move-in date is not after today.
 *   2. Each move-in date is before the move-in date of the next address,
 *      because an address ends on the day that the next address starts.
 *   3. When the client completes the form, the three addresses cover 36
 *      months. A save does not check it, because a client saves a
 *      half-filled section.
 */
export const CLIENT_APPLICATION_ADDRESS_HISTORY_RULES: readonly ClientApplicationFormRule[] = [
    {
        id: 'address history dates not in the future',
        check: (answers, context) => {
            const today = context.today;
            if (today === null) return [];
            return shownMovedInDates(answers, context)
                .filter((entry) => entry.date !== null && entry.date > today)
                .map((entry) => ({
                    questionId: entry.id,
                    message: ADDRESS_HISTORY_FUTURE_MESSAGE,
                }));
        },
    },
    {
        id: 'address history dates in order',
        check: (answers, context) => {
            if (context.today === null) return [];
            const shown = shownMovedInDates(answers, context);
            const errors: ClientApplicationRuleError[] = [];
            for (let index = 1; index < shown.length; index++) {
                const later = shown[index - 1]!;
                const earlier = shown[index]!;
                if (later.date === null || earlier.date === null) continue;
                if (earlier.date >= later.date) {
                    errors.push({
                        questionId: earlier.id,
                        message: addressHistoryOrderMessage(index === 1 ? 'current' : 'previous'),
                    });
                }
            }
            return errors;
        },
    },
    {
        id: 'address history covers 36 months',
        check: (answers, context) => {
            const today = context.today;
            if (today === null || !context.requireComplete) return [];
            if (!addressHistoryNeedsThird(answers, context)) return [];
            const third = calendarDate(answers[CLIENT_APPLICATION_MOVED_IN_DATE_IDS.third]);
            if (third === null || coversAddressHistory(third, today)) return [];
            return [
                {
                    questionId: CLIENT_APPLICATION_MOVED_IN_DATE_IDS.third,
                    message: ADDRESS_HISTORY_COVERAGE_MESSAGE,
                },
            ];
        },
    },
];
